import React from 'react';
import {ChevronLeftIcon, ChevronRightIcon} from "@heroicons/react/solid";
import DataPage from "types/DataPage";
import {concatenate} from "services/string";
import DataPageLink from "types/DataPageLink";
import DataResource from "types/DataResource";

// Types ----

type PaginatorProps = {
  data: DataPage<any> | DataResource<any>
  setPage: (page: number) => void
  isFetching?: boolean
  hideBorder?: boolean
}

// Helpers ----

function extractMetadata(data: DataPage<any> | DataResource<any>) {
  if ("meta" in data) {
    return data.meta
  }

  return (({current_page, last_page, from, to, total, per_page, links}) => ({
    current_page, last_page, from, to, total, per_page, links
  }))(data)
}

// Component ----

function Paginator({data, setPage, hideBorder}: PaginatorProps) {
  const meta = extractMetadata(data);

  function selectPage(link: DataPageLink) {
    const matches = /page=(\d+)/.exec(link.url)
    const page = matches ? matches[1] : null

    if (page) {
      setPage(Number(page))
    }
  }

  return (
    <div
      className={concatenate("bg-white py-3 flex items-center justify-between", !hideBorder && "border-t border-gray-200 px-4 sm:px-6")}>
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          className={concatenate(meta.current_page === 1 ? 'bg-gray-100 text-gray-400' : 'text-gray-700 bg-white hover:bg-gray-50', 'relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md')}
          onClick={() => setPage(meta.current_page - 1)}
          disabled={meta.current_page === 1}>
          Previous
        </button>
        <button
          className={concatenate(meta.current_page === meta.last_page ? 'bg-gray-100 text-gray-400' : 'text-gray-700 bg-white hover:bg-gray-50', 'relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md')}
          onClick={() => setPage(meta.current_page + 1)}
          disabled={meta.current_page === meta.last_page}>
          Next
        </button>
      </div>
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            {!!meta.from && !!meta.to &&
              <>
                Showing <span className="font-medium">{meta.from.toLocaleString()}</span>
                {' '}to <span className="font-medium">{meta.to.toLocaleString()}</span>
                {' '}of <span className="font-medium">{meta.total.toLocaleString()}</span> results
              </>
            }

            {(!meta.from || !meta.to) &&
              <>
                <span className="font-medium">{meta.total?.toLocaleString()}</span> results
              </>
            }
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              className={concatenate(meta.current_page === 1 ? 'bg-gray-100 text-gray-300' : 'bg-white hover:bg-gray-50 text-gray-500', 'relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium')}
              onClick={() => setPage(meta.current_page - 1)}
              disabled={meta.current_page === 1}>
              <span className="sr-only">Previous</span>
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true"/>
            </button>

            {meta.links?.filter((_link, index) => index > 0 && index < meta.links.length - 1).map((link) => (
              <button
                key={link.label}
                className={concatenate(
                  link.active ? 'z-10 bg-brand/20 border-brand text-brand' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-4 py-2 border text-sm font-medium')}
                onClick={() => selectPage(link)}>
                {link.label}
              </button>
            ))}

            <button
              className={concatenate(meta.current_page === meta.last_page ? 'bg-gray-100 text-gray-300' : 'bg-white hover:bg-gray-50 text-gray-500', 'relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium')}
              onClick={() => setPage(meta.current_page + 1)}
              disabled={meta.current_page === meta.last_page}>
              <span className="sr-only">Next</span>
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true"/>
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

// Export ----

export default Paginator;